import React, { forwardRef } from 'react';

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export const DigitalCard = forwardRef(function DigitalCard({ card }, ref) {
  if (!card) return null;

  const photoSrc = card.photo_url
    ? (card.photo_url.startsWith('http') ? card.photo_url : `${BACKEND}${card.photo_url}`)
    : null;

  return (
    <div
      ref={ref}
      className="w-full max-w-md mx-auto rounded-2xl overflow-hidden shadow-xl relative"
      style={{
        aspectRatio: '1.586',
        background: 'linear-gradient(135deg, #007FFF 0%, #0056B3 40%, #CE1021 70%, #F7D618 100%)'
      }}
      data-testid="digital-card"
    >
      {/* Pattern overlay */}
      <div className="absolute inset-0 opacity-10" style={{
        backgroundImage: 'url("data:image/svg+xml,%3Csvg width=\'60\' height=\'60\' viewBox=\'0 0 60 60\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cg fill=\'none\' fill-rule=\'evenodd\'%3E%3Cg fill=\'%23ffffff\' fill-opacity=\'0.4\'%3E%3Cpath d=\'M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z\'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")',
      }} />

      <div className="relative h-full p-4 sm:p-5 flex flex-col justify-between text-white">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <p className="text-[9px] sm:text-[10px] uppercase tracking-widest opacity-80">Republique Democratique du Congo</p>
            <h3 className="text-xs sm:text-sm font-bold mt-0.5" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Carte de Congolais de l'Etranger
            </h3>
          </div>
          <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-full bg-white/20 flex items-center justify-center backdrop-blur-sm">
            <span className="text-base sm:text-lg font-bold">CD</span>
          </div>
        </div>

        {/* Body with photo */}
        <div className="flex gap-3 items-stretch">
          {/* Passport photo */}
          <div className="shrink-0">
            <div
              className="w-16 h-20 sm:w-20 sm:h-24 rounded-lg border-2 border-white/40 overflow-hidden flex items-center justify-center"
              style={{ backgroundColor: photoSrc ? 'transparent' : 'rgba(255,255,255,0.15)' }}
            >
              {photoSrc ? (
                <img src={photoSrc} alt="Photo" className="w-full h-full object-cover" crossOrigin="anonymous" />
              ) : (
                <div className="text-center">
                  <svg className="w-8 h-8 mx-auto opacity-50" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                    <circle cx="12" cy="7" r="4" />
                  </svg>
                  <p className="text-[7px] uppercase opacity-50 mt-0.5">Photo</p>
                </div>
              )}
            </div>
          </div>

          {/* Info */}
          <div className="flex-1 space-y-1.5 min-w-0">
            <div>
              <p className="text-[8px] sm:text-[9px] uppercase tracking-wider opacity-70">Nom Complet</p>
              <p className="text-sm sm:text-base font-bold truncate" style={{ fontFamily: 'Outfit, sans-serif' }}>
                {card.full_name || 'Nom Prenom'}
              </p>
            </div>
            <div className="flex gap-3">
              <div>
                <p className="text-[8px] sm:text-[9px] uppercase tracking-wider opacity-70">Naissance</p>
                <p className="text-[10px] sm:text-xs font-medium">{card.date_of_birth || '--/--/----'}</p>
              </div>
              <div>
                <p className="text-[8px] sm:text-[9px] uppercase tracking-wider opacity-70">Nationalite</p>
                <p className="text-[10px] sm:text-xs font-medium">{card.nationality || 'Congolaise'}</p>
              </div>
              <div>
                <p className="text-[8px] sm:text-[9px] uppercase tracking-wider opacity-70">Residence</p>
                <p className="text-[10px] sm:text-xs font-medium truncate">{card.country_of_residence || '-'}</p>
              </div>
            </div>
            <div>
              <p className="text-[8px] sm:text-[9px] uppercase tracking-wider opacity-70">N de Carte</p>
              <p className="text-xs sm:text-sm font-bold tracking-wider" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                {card.card_number || 'CE-0000-000000'}
              </p>
            </div>
          </div>

          {/* QR Code */}
          {card.qr_code_data && (
            <div className="w-16 h-16 sm:w-20 sm:h-20 bg-white rounded-lg p-1 shrink-0 self-end">
              <img src={`data:image/png;base64,${card.qr_code_data}`} alt="QR Code" className="w-full h-full" />
            </div>
          )}
        </div>

        {/* Footer stripe */}
        <div className="flex items-center justify-between mt-1">
          <div className="flex gap-1">
            <div className="w-5 h-1.5 rounded-full bg-[#007FFF]" />
            <div className="w-5 h-1.5 rounded-full bg-[#CE1021]" />
            <div className="w-5 h-1.5 rounded-full bg-[#F7D618]" />
          </div>
          <p className="text-[8px] sm:text-[9px] opacity-60">
            Valide jusqu'au {card.expires_at ? new Date(card.expires_at).toLocaleDateString('fr-FR') : '--/--/----'}
          </p>
        </div>
      </div>
    </div>
  );
});
